import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ClipboardPayload,
  ClipboardUnavailableError,
  payloadFromDataTransfer,
  readClipboardPayload,
} from './clipboardPayload';

/**
 * Runs a clipboard read on demand. The happy path is
 * `navigator.clipboard.read()` (Chromium). When that is unavailable or denied
 * (Firefox, insecure context) it *arms* a one-shot capture: a visually-hidden
 * `contentEditable` is focused and the user's next Ctrl/⌘+V is read from the
 * native `paste` event. Either way `onPayload` is called with the same shape.
 */

const ARMED_TIMEOUT_MS = 15000;

/** Local-dev-only visibility into what actually landed in the clipboard payload. */
function logPastePayload(source: string, payload: ClipboardPayload): void {
  if (process.env.NODE_ENV !== 'development') return;
  console.log(`[ClipboardPaste:${source}]`, payload);
}

const HIDDEN_STYLE: React.CSSProperties = {
  position: 'fixed',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
  opacity: 0,
};

export interface HiddenPasteInputProps {
  ref: React.RefObject<HTMLDivElement | null>;
  contentEditable: boolean;
  suppressContentEditableWarning: boolean;
  'aria-hidden': boolean;
  role: string;
  tabIndex: number;
  onPaste: (event: React.ClipboardEvent) => void;
  onBlur: () => void;
  style: React.CSSProperties;
}

export interface UseClipboardPaste {
  run: () => void;
  busy: boolean;
  error: string | null;
  armed: boolean;
  clearError: () => void;
  hiddenInputProps: HiddenPasteInputProps;
}

export function useClipboardPaste(
  onPayload: (payload: ClipboardPayload) => void,
): UseClipboardPaste {
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hiddenRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disarm = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setArmed(false);
  }, []);

  useEffect(() => disarm, [disarm]);

  const arm = useCallback(() => {
    setError(null);
    setArmed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(disarm, ARMED_TIMEOUT_MS);
    setTimeout(() => hiddenRef.current?.focus(), 0);
  }, [disarm]);

  const run = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = await readClipboardPayload();
      logPastePayload('read', payload);
      onPayload(payload);
    } catch (err) {
      if (err instanceof ClipboardUnavailableError) arm();
      else setError((err as Error)?.message ?? 'Could not read the clipboard.');
    } finally {
      setBusy(false);
    }
  }, [arm, onPayload]);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      event.preventDefault();
      disarm();
      const payload = payloadFromDataTransfer(event.clipboardData);
      logPastePayload('paste-event', payload);
      onPayload(payload);
    },
    [disarm, onPayload],
  );

  return {
    run: () => void run(),
    busy,
    error,
    armed,
    clearError: () => setError(null),
    hiddenInputProps: {
      ref: hiddenRef,
      contentEditable: true,
      suppressContentEditableWarning: true,
      'aria-hidden': true,
      role: 'textbox',
      tabIndex: -1,
      onPaste: handlePaste,
      onBlur: disarm,
      style: HIDDEN_STYLE,
    },
  };
}
